import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllCampaignsSummary.graphql';
import { Query, QueryallCampaignsSummaryArgs } from '../../__generated__/globalTypes';

export const useAllCampaignsSummaryQuery = (options?: QueryHookOptions<Query, QueryallCampaignsSummaryArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallCampaignsSummaryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllCampaignsSummaryLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallCampaignsSummaryArgs>
): QueryResult<Query, QueryallCampaignsSummaryArgs> & {
  getAllCampaignsSummary: LazyQueryExecFunction<Query, QueryallCampaignsSummaryArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllCampaignsSummary, result] = useLazyQuery<Query, QueryallCampaignsSummaryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllCampaignsSummary, ...result };
};

export const useAllCampaignsSummaryPromiseQuery = () => {
  const client = useApolloClient();

  const getAllCampaignsSummary = useCallback(
    (options?: Omit<QueryOptions<QueryallCampaignsSummaryArgs, Query>, 'query'>) =>
      client.query<Query, QueryallCampaignsSummaryArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllCampaignsSummary };
};
// Suspense Query Hooks
export const useAllCampaignsSummarySuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallCampaignsSummaryArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallCampaignsSummaryArgs>(QUERY, {
    ...options
  });

  return result;
};
